import React from 'react';

import { useBillingInformationQuery } from '@edx/graphql/generated';
import { useAuth } from '@edx/hooks/use-auth';
import { IconPaymentVisa, IconPaymentMastercard, IconPaymentAmex } from '@edx/components/icons';
import { Button } from '@edx/components/button';
import { Link } from '@edx/components/link';
import { Body, Caption, Title } from '@edx/components/typography';
import { useRouter } from 'next/router';

export const BillingPlansTable: React.FC = () => {
  const router = useRouter();
  const { isLoggedIn } = useAuth();
  const { data } = useBillingInformationQuery();
  const [monthlyPlan, quarterlyPlan, yearlyPlan] = data?.billingInformation.plans || [];

  const decorateWithPlan = (plan: string): string => {
    const ctaLink = isLoggedIn ? '/suscripcion' : '/registrate';

    const showSimpleNavBar = router.query?.nav === 'f';
    const queryParams = showSimpleNavBar ? `plan=${plan}&nav=f` : `plan=${plan}`;

    return `${ctaLink}?${queryParams}`;
  };

  return (
    <section className="grid grid-cols-12">
      <div className="col-start-1 md:col-start-2 lg:col-start-3 col-span-12 md:col-span-10 lg:col-span-8 px-0 md:px-4">
        <header className="bg-gray-800 p-6 flex justify-between">
          <Title tag="h2" size="small" className="text-white leading-tight">
            Selecciona tu suscripción
          </Title>

          <ul className="flex items-center space-x-1">
            <li>
              <IconPaymentVisa />
            </li>
            <li>
              <IconPaymentMastercard />
            </li>
            <li>
              <IconPaymentAmex />
            </li>
          </ul>
        </header>

        <ul className="bg-white grid grid-rows-3 sm:grid-rows-1 gap-2 sm:gap-0 grid-cols-1 sm:grid-cols-3 sm:p-0 p-6">
          <li className="bg-white border font-body border-gray-400 md:border-transparent" aria-label="plan">
            <div className="px-4 py-1 bg-gray-300">
              <span className="uppercase font-body font-medium text-xs tracking-widest text-primary-500">
                {'\u00a0'}
              </span>
            </div>
            <div className="flex flex-col p-4">
              <Body className="text-gray-500">Mensual</Body>
              <Title size="large">
                {monthlyPlan?.priceFormatted || '\u00a0'}
                {monthlyPlan?.needsClarification && <sup className="text-sm align-top">{monthlyPlan.currency}</sup>}
              </Title>
              <Body className="text-gray-600">al mes</Body>
            </div>
            <div className="px-4 py-2">
              <Button btnType="block" variant="outlineLight" textVariant="medium" href={decorateWithPlan('monthly')}>
                Suscríbete
              </Button>
            </div>
          </li>

          <li
            className="bg-white border font-body border-2 border-primary-500 md:transform md:scale-105"
            aria-label="plan"
          >
            <div className="px-4 py-1 bg-primary-500">
              <Caption tag="span" variant="semibold" className="text-white">
                Top ventas
              </Caption>
            </div>
            <div className="flex flex-col p-4">
              <Body className="text-gray-500">Trimestral</Body>
              <Title size="large">
                {quarterlyPlan?.priceFormatted || '\u00a0'}
                {quarterlyPlan?.needsClarification && <sup className="text-sm align-top">{quarterlyPlan.currency}</sup>}
              </Title>
              <Body className="text-gray-600">cada 3 meses</Body>
            </div>
            <div className="px-4 py-2">
              <Button btnType="block" variant="primary" textVariant="medium" href={decorateWithPlan('quarterly')}>
                Suscríbete
              </Button>

              <Body size="small" className="my-2 text-center text-gray-500">
                {quarterlyPlan?.monthlySavings ? `Ahorra ${quarterlyPlan?.monthlySavings}` : '\u00a0'}
              </Body>
            </div>
          </li>

          <li className="bg-white border font-body border-gray-400 md:border-transparent" aria-label="plan">
            <div className="px-4 py-1 bg-primary-500 bg-opacity-10">
              <Caption tag="span" variant="semibold" className="text-primary-500">
                Mejor oferta
              </Caption>
            </div>
            <div className="flex flex-col p-4">
              <Body className="text-gray-500">Anual</Body>
              <Title size="large">
                {yearlyPlan?.priceFormatted || '\u00a0'}
                {yearlyPlan?.needsClarification && <sup className="text-sm align-top">{yearlyPlan.currency}</sup>}
              </Title>
              <Body className="text-gray-600">al año</Body>
            </div>
            <div className="px-4 py-2">
              <Button btnType="block" variant="outlineLight" textVariant="medium" href={decorateWithPlan('yearly')}>
                Suscríbete
              </Button>

              <Body size="small" className="my-2 text-center text-gray-500">
                {yearlyPlan?.monthlySavings ? `¡Ahorra ${yearlyPlan?.monthlySavings}!` : '\u00a0'}
              </Body>
            </div>
          </li>
        </ul>

        {!isLoggedIn && (
          <footer className="font-body mt-8 text-center text-gray-500">
            * Si deseas tener acceso sólo a la comunidad,{' '}
            <Link href="/registrate?plan=free" className="font-medium text-gray-600 hover:underline">
              regístrate aquí
            </Link>
          </footer>
        )}
      </div>
    </section>
  );
};
